import React from "react";
import { TextField, Typography, useTheme } from "@mui/material";
import AssistantPhotoIcon from "@mui/icons-material/AssistantPhoto";
import { COUNTRY_LIST } from "data/static";
import { CustomModal, ModalBody, ModalHeader } from "./CustomModal";
import { useAuth } from "hooks";

export const CustomCountryPhoneInput = React.forwardRef((props, ref) => {
  const { viewEntity } = useAuth();

  const { onChange, value = "", fullWidth = false, ...rest } = props;
  const theme = useTheme(); // Access Material-UI theme

  // States
  const [isCountryCodeAppended, setIsCountryCodeAppended] =
    React.useState(false);
  const [realValue, setRealValue] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isCountrySelectModalActive, setIsCountrySelectModalActive] =
    React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState(null);

  // Handlers
  const onCloseModal = () => {
    setSearchTerm("");
    setIsCountrySelectModalActive(false);
  };

  const onClickCountry = (country) => {
    setSelectedCountry(country);
    setIsCountrySelectModalActive(false);
    setSearchTerm("");

    let updatedValue = "";
    if (value.includes("-")) {
      updatedValue = value.split("-")[1];
    } else {
      updatedValue = value?.replace("+", "");
    }

    onChange(`${country?.idd}-${updatedValue}-${country?.cca2}`);
  };

  // Effects
  React.useEffect(() => {
    if (value?.toString()?.includes("-")) {
      setRealValue(value?.toString()?.split("-")[1] || "");
    } else {
      setRealValue(value?.toString()?.replace("+", ""));
      setSelectedCountry(viewEntity?.entity?.country);
    }

    if (value?.toString()?.split("-")?.length === 3) {
      const countryIdd = value?.toString()?.split("-")[0];
      const country = COUNTRY_LIST.find((c) => c?.idd === countryIdd);
      setSelectedCountry(country);
    }
  }, [value, viewEntity?.entity?.country]);

  React.useEffect(() => {
    if (typeof value === "string" && value?.includes("-")) {
      const idd = value?.split("-")[0];
      const countryCCA2 = value?.split("-")[2];
      let country = null;

      if (countryCCA2) {
        country = COUNTRY_LIST.find((c) => c.cca2 === countryCCA2);
      } else {
        country = COUNTRY_LIST.find((c) => c.idd === idd);
      }

      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value]); // Empty dependency array to avoid re-rendering issues

  return (
    <div
      className="flex w-full items-center gap-2"
      style={{ width: fullWidth ? "100%" : undefined }}
    >
      {/* Country Flag Container */}
      <div
        className="flex h-10 w-12 cursor-pointer items-center justify-center rounded"
        style={{
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
        onClick={() => setIsCountrySelectModalActive(true)}
      >
        {selectedCountry ? (
          <img
            style={{
              width: "50px",
              height: "34px",
              objectFit:
                selectedCountry?.name === "Nepal" ? "contain" : "cover",
              borderRadius: "3px",
            }}
            src={selectedCountry.flagUrl.png}
            alt={`${selectedCountry.name} Flag`}
          />
        ) : (
          <AssistantPhotoIcon style={{ color: theme.palette.primary.main }} />
        )}
      </div>

      {/* Country Code and Input */}
      <div className="flex w-full items-center gap-2">
        {selectedCountry && (
          <div
            className="flex h-full items-center justify-center rounded border px-2"
            style={{
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.background.default,
              paddingBlock: "10px",
            }}
          >
            <span
              className="input-group-text"
              style={{ fontSize: 13, color: theme.palette.text.primary }}
            >
              {selectedCountry.idd}
            </span>
          </div>
        )}
        <TextField
          {...rest}
          className="w-full py-2"
          ref={ref}
          size="small"
          type="tel"
          value={realValue}
          minLength={8}
          maxLength={16}
          onChange={(e) => {
            let value = "";
            setRealValue(e.target.value);

            if (!selectedCountry?.idd) {
              value = e.target.value;
            } else {
              value =
                `${selectedCountry?.idd}-${e.target.value}-${selectedCountry?.cca2}` ||
                "";
            }

            onChange(value);
            if (selectedCountry && !isCountryCodeAppended) {
              setIsCountryCodeAppended(true);
            }
          }}
          InputProps={{
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          }}
        />
      </div>

      {/* Country Selection Modal */}
      {isCountrySelectModalActive && (
        <CustomModal
          classNames="country-phone-modal"
          height={500}
          open={isCountrySelectModalActive}
          onClose={() => onCloseModal()}
          PaperProps={{
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          }}
        >
          <ModalHeader>Select a country</ModalHeader>

          <ModalBody>
            <div className="relative">
              {/* Country Input Filter */}
              <div className="sticky top-0 left-0 z-10 w-full py-3">
                <input
                  type="text"
                  className="form-control form-font w-full border py-2"
                  placeholder="Search country"
                  style={{
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    paddingBlock: "9px",
                  }}
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Countries List */}
              <div
                style={{
                  height: "300px",
                  maxHeight: "300px",
                  overflow: "auto",
                  marginTop: "1rem",
                }}
              >
                {COUNTRY_LIST.filter(
                  (c) =>
                    c.name
                      ?.toLowerCase()
                      ?.includes(searchTerm?.trim()?.toLowerCase()) ||
                    c.idd
                      ?.toLowerCase()
                      ?.includes(searchTerm?.trim()?.toLowerCase())
                ).map((country, idx) => (
                  <div
                    tabIndex={0}
                    key={idx}
                    className="hover: mb-2 flex cursor-pointer items-center justify-between rounded border px-2 py-2"
                    style={{
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => onClickCountry(country)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") {
                        onClickCountry(country);
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        style={{
                          width: "24px",
                          height: "24px",
                          objectFit: "cover",
                          borderRadius: "3px",
                        }}
                        src={country.flagUrl.png}
                        alt={`${country.name} Flag`}
                      />
                      <Typography style={{ color: theme.palette.text.primary }}>
                        {country.name}
                      </Typography>
                    </div>
                    <Typography style={{ color: theme.palette.text.secondary }}>
                      {country.idd}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </ModalBody>
        </CustomModal>
      )}
    </div>
  );
});
